/**
 * Edge Functions Client
 *
 * Provides utilities for calling Supabase Edge Functions from the client.
 *
 * All edge function calls route through `callEdgeFunction()` for consistent:
 * - Auth token management (via Supabase SDK)
 * - Timeout handling (configurable, default 30s, max 120s)
 * - Mock mode behavior (returns mock data when provided, not errors)
 * - Error shape ({ data, error })
 *
 * Mode behavior:
 * - MOCK MODE: Returns mock data (if provided) or null data with descriptive error
 * - DEMO MODE: Calls real edge functions with demo backend
 * - PRODUCTION MODE: Calls real edge functions with production backend
 */

import type { ClaimsSummary } from '@/lib/scoring/backing-status';
import { supabase } from './client';
import { mode } from '@/lib/config/app-mode';
import { CLIENT_TIMEOUT_CODE } from './edge-timeout';
import { isDemoModeTrusted } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import type {
  InterviewStartResponse,
  InterviewMessageResponse,
  InterviewCompleteResponse,
  InterviewDigestResponse,
} from '@/lib/interview/types';

// Edge function response type
export interface EdgeFunctionResponse<T> {
  data: T | null;
  error: string | null;
  /**
   * Machine-readable error code, when the edge function's error body carries
   * one (e.g. onboarding-claim's 409 `ALREADY_OWNED`). Undefined for success
   * responses and for errors that don't set a `code` field — callers should
   * fall back to the `error` message.
   */
  code?: string;
}

// Options for callEdgeFunction
interface CallEdgeFunctionOptions<T> {
  /** Timeout in milliseconds (default: 30000, max: 120000) */
  timeout?: number;
  /** Mock data to return in mock mode. If provided, mock mode returns { data: mockData, error: null } */
  mockData?: T;
  /** Enable request deduplication — identical inflight calls share one promise */
  dedupe?: boolean;
}

// Default and maximum timeout constants
const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_TIMEOUT_MS = 180_000;

// Inflight request map for deduplication
// Key: `functionName:serializedBody`, Value: in-progress promise
const inflightRequests = new Map<string, Promise<EdgeFunctionResponse<any>>>();

/** Clear a specific inflight dedupe entry (e.g. after user cancels an analysis) */
export function clearInflightRequest(functionName: string, keyPart?: string): void {
  const dedupeKey = `${functionName}:${keyPart ?? ''}`;
  inflightRequests.delete(dedupeKey);
}

/**
 * Call a Supabase Edge Function
 *
 * Unified entry point for all edge function calls. Handles auth, timeout,
 * mock mode, and error formatting consistently.
 *
 * @param functionName - The name of the edge function
 * @param body - The request body
 * @param options - Optional configuration (timeout, mockData)
 * @returns The response data or error
 */
export async function callEdgeFunction<T>(
  functionName: string,
  body?: object,
  options?: CallEdgeFunctionOptions<T>,
): Promise<EdgeFunctionResponse<T>> {
  // In MOCK mode (true offline mock, NOT demo bypass), return mock data if provided
  if (!mode.useRealAPI) {
    if (options?.mockData !== undefined) {
      return {
        data: options.mockData,
        error: null,
      };
    }
    return {
      data: null,
      error: 'Edge functions not available in mock mode. Switch to demo or production mode.',
    };
  }

  // Request deduplication — if an identical call is already in-flight, return its promise
  // Use lightweight key (function name + file name if present) instead of serializing entire body
  if (options?.dedupe) {
    const bodyObj = body as Record<string, unknown> | undefined;
    const keyPart = bodyObj?.fileName || bodyObj?.startupId || '';
    const dedupeKey = `${functionName}:${keyPart}`;
    const existing = inflightRequests.get(dedupeKey);
    if (existing) {
      if (__DEV__) console.log(`[edge-functions] ${functionName}: dedupe hit`);
      return existing as Promise<EdgeFunctionResponse<T>>;
    }

    const promise = callEdgeFunctionInternal<T>(functionName, body, options).finally(() => {
      inflightRequests.delete(dedupeKey);
    });
    inflightRequests.set(dedupeKey, promise);
    return promise;
  }

  return callEdgeFunctionInternal<T>(functionName, body, options);
}

/**
 * Internal implementation of edge function call (separated for dedup wrapper)
 */
async function callEdgeFunctionInternal<T>(
  functionName: string,
  body?: object,
  options?: CallEdgeFunctionOptions<T>,
): Promise<EdgeFunctionResponse<T>> {
  const timeoutMs = Math.min(options?.timeout ?? DEFAULT_TIMEOUT_MS, MAX_TIMEOUT_MS);

  try {
    // Get current session, refreshing if needed
    let { data: sessionData } = await supabase.auth.getSession();
    let session = sessionData?.session;

    // Check if token is expired or expiring within 60s — force refresh
    if (session) {
      const expiresAt = session.expires_at ?? 0; // unix seconds
      const nowSec = Math.floor(Date.now() / 1000);
      if (expiresAt - nowSec < 60) {
        const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session) {
          reportError(refreshErr ?? 'Session refresh returned no session', { context: 'callEdgeFunction.sessionRefresh', silent: true });
        } else {
          session = refreshed.session;
        }
      }
    }

    if (!session) {
      // In demo mode, attempt re-authentication with demo credentials from env.
      // S11: gate on isDemoModeTrusted() — NOT isDemoMode(). The bundled demo
      // credentials may only auto-login when demo mode is declared by the build
      // (EXPO_PUBLIC_APP_MODE=demo) or host, never when a production-origin build
      // was flipped to demo via the client-writable persisted override.
      const demoEmail = process.env.EXPO_PUBLIC_DEMO_EMAIL;
      const demoPassword = process.env.EXPO_PUBLIC_DEMO_PASSWORD;
      if (isDemoModeTrusted() && demoEmail && demoPassword) {
        try {
          const { data: signInData } = await supabase.auth.signInWithPassword({
            email: demoEmail,
            password: demoPassword,
          });
          if (signInData?.session) {
            session = signInData.session;
          }
        } catch (e) {
          reportError(e, { context: 'callEdgeFunction.demoReAuth', silent: true });
        }
      }

      if (!session) {
        return { data: null, error: 'Not authenticated. Please log in again.' };
      }
    }

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

    const makeRequest = async (accessToken: string) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const jsonBody = JSON.stringify(body || {});
      const fetchStart = Date.now();

      const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': anonKey,
        },
        body: jsonBody,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (__DEV__) console.log(`[edge-functions] ${functionName}: ${response.status} in ${Date.now() - fetchStart}ms`);
      return response;
    };

    let response = await makeRequest(session.access_token);

    // On 401, try refreshing the session once and retry
    if (response.status === 401) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) {
        session = refreshed.session;
        response = await makeRequest(session.access_token);
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const errorMsg = responseData?.error || responseData?.message || `Edge function ${functionName} failed (HTTP ${response.status})`;
      return {
        data: null,
        error: errorMsg,
        code: typeof responseData?.code === 'string' ? responseData.code : undefined,
      };
    }

    return {
      data: responseData as T,
      error: null,
    };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      // A CLIENT-SIDE abort. The request was not cancelled server-side: the
      // edge function keeps running and COMMITS its work. Callers that kick off
      // long, committing pipelines (deck analysis) must not report this as a
      // failure — on 2026-07-31 `machineMD` was created, scored, promoted
      // privately and given a deck logo while the UI showed "Analysis Failed".
      // The `code` is what lets a caller tell "we stopped listening" apart from
      // "the server said no".
      return {
        data: null,
        error: `Still processing after ${Math.round(timeoutMs / 1000)}s.`,
        code: CLIENT_TIMEOUT_CODE,
      };
    }
    reportError(err, { context: `callEdgeFunction:${functionName}`, silent: true });
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

// ============================================================================
// Stripe Functions
// ============================================================================

export interface CreateCheckoutRequest {
  tier: 'starter' | 'standard' | 'premium';
  billingInterval: 'month' | 'year';
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCheckoutResponse {
  sessionId: string;
  url: string;
}

export async function createCheckoutSession(
  params: CreateCheckoutRequest
): Promise<EdgeFunctionResponse<CreateCheckoutResponse>> {
  return callEdgeFunction<CreateCheckoutResponse>('stripe-checkout', params, {
    mockData: {
      sessionId: 'mock_session_id',
      url: params.successUrl,
    },
  });
}

export interface CreatePortalRequest {
  returnUrl: string;
}

export interface CreatePortalResponse {
  url: string;
}

export async function createPortalSession(
  params: CreatePortalRequest
): Promise<EdgeFunctionResponse<CreatePortalResponse>> {
  return callEdgeFunction<CreatePortalResponse>('stripe-portal', params, {
    mockData: {
      url: params.returnUrl,
    },
  });
}

export interface UpdateSubscriptionRequest {
  newTier: 'starter' | 'standard' | 'premium';
  billingInterval?: 'month' | 'year';
}

export interface UpdateSubscriptionResponse {
  success: boolean;
  subscription?: {
    id: string;
    tier: string;
    status: string;
    billingInterval: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
  };
  error?: string;
}

export async function updateSubscription(
  params: UpdateSubscriptionRequest
): Promise<EdgeFunctionResponse<UpdateSubscriptionResponse>> {
  return callEdgeFunction<UpdateSubscriptionResponse>('stripe-update-subscription', params, {
    mockData: {
      success: true,
      subscription: {
        id: 'mock_sub_id',
        tier: params.newTier,
        status: 'active',
        billingInterval: params.billingInterval || 'month',
        currentPeriodStart: new Date().toISOString(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
    },
  });
}

// ============================================================================
// Cancel Subscription
// ============================================================================

export interface CancelSubscriptionRequest {
  cancelImmediately?: boolean;
  reason?: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  subscription?: {
    id: string;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string;
    canceledAt: string | null;
  };
  message?: string;
  error?: string;
}

export async function cancelSubscription(
  params: CancelSubscriptionRequest = {}
): Promise<EdgeFunctionResponse<CancelSubscriptionResponse>> {
  return callEdgeFunction<CancelSubscriptionResponse>('stripe-cancel-subscription', params, {
    mockData: {
      success: true,
      subscription: {
        id: 'mock_sub_id',
        status: params.cancelImmediately ? 'canceled' : 'active',
        cancelAtPeriodEnd: !params.cancelImmediately,
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        canceledAt: new Date().toISOString(),
      },
      message: params.cancelImmediately
        ? 'Subscription canceled immediately'
        : 'Subscription will cancel at end of billing period',
    },
  });
}

// ============================================================================
// Reactivate Subscription
// ============================================================================

export interface ReactivateSubscriptionResponse {
  success: boolean;
  subscription?: {
    id: string;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string;
  };
  message?: string;
  error?: string;
}

export async function reactivateSubscription(): Promise<EdgeFunctionResponse<ReactivateSubscriptionResponse>> {
  return callEdgeFunction<ReactivateSubscriptionResponse>('stripe-reactivate-subscription', undefined, {
    mockData: {
      success: true,
      subscription: {
        id: 'mock_sub_id',
        status: 'active',
        cancelAtPeriodEnd: false,
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
      message: 'Subscription reactivated successfully',
    },
  });
}

// ============================================================================
// Team Functions
// ============================================================================

export interface TeamInviteRequest {
  email: string;
  role?: 'admin' | 'member';
  // Task 13: the inviter picks Co-founder vs Team member; the edge function
  // derives the access `role` from this (never independently trusted from
  // the client `role` above once founderStatus is present). REQUIRED — the
  // server's `teamInviteRequestSchema` requires it too, so an optional type
  // here just defers a 400 to runtime (final-review MINOR).
  founderStatus: 'co_founder' | 'team_member';
}

export interface TeamInviteResponse {
  success: boolean;
  inviteId: string;
  expiresAt: string;
  token: string;
}

export async function sendTeamInvite(
  params: TeamInviteRequest
): Promise<EdgeFunctionResponse<TeamInviteResponse>> {
  return callEdgeFunction<TeamInviteResponse>('team-invite', params, {
    mockData: {
      success: true,
      inviteId: `mock_invite_${Date.now()}`,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      token: `mock_token_${Date.now()}`,
    },
  });
}

export interface AcceptTeamInviteRequest {
  token: string;
}

export interface AcceptTeamInviteResponse {
  success: boolean;
  teamId: string;
  teamName: string;
}

export async function acceptTeamInvite(
  params: AcceptTeamInviteRequest
): Promise<EdgeFunctionResponse<AcceptTeamInviteResponse>> {
  return callEdgeFunction<AcceptTeamInviteResponse>('team-accept-invite', params, {
    mockData: {
      success: true,
      teamId: 'mock_team_id',
      teamName: 'Demo Team',
    },
  });
}

export interface PreviewTeamInviteRequest {
  token: string;
}

/**
 * Read-only preview of a pending team invite (onboarding redesign Task 5).
 * Backs the explicit invitation-preview screen at `/invite/team`: it resolves
 * the invite by token WITHOUT accepting it (no membership insert, no
 * markAccepted), so the invitee can review company / inviter / invited-email /
 * assigned status and then choose Accept or Decline. Acceptance stays a
 * separate, explicit call to `acceptTeamInvite`.
 *
 * `companyName`/`inviterName` may be null when the team is unnamed or the
 * inviter profile is unresolved — the UI renders those generically.
 * `emailMatches` is false when the signed-in email differs from the invited
 * one; the UI uses it to gate the Accept action.
 */
export interface PreviewTeamInviteResponse {
  success: boolean;
  companyName: string | null;
  inviterName: string | null;
  invitedEmail: string;
  role: string;
  founderStatus: string | null;
  emailMatches: boolean;
}

export async function previewTeamInvite(
  params: PreviewTeamInviteRequest
): Promise<EdgeFunctionResponse<PreviewTeamInviteResponse>> {
  return callEdgeFunction<PreviewTeamInviteResponse>(
    'team-accept-invite',
    { ...params, action: 'preview' },
    {
      mockData: {
        success: true,
        companyName: 'Northwind Labs',
        inviterName: 'Alex Founder',
        invitedEmail: 'you@example.com',
        role: 'member',
        founderStatus: 'team_member',
        emailMatches: true,
      },
    }
  );
}

// ============================================================================
// Onboarding — Company Anchor Functions (Task 2.3, startup-member onboarding
// plan — docs/superpowers/plans/2026-07-09-startup-member-onboarding.md).
// Backs the company-anchor screen's Claim/Join/Create actions (Create is a
// direct `startup_profiles` insert, no edge function — see
// startup-company.tsx). `claimCompany` is a BRIDGE onto the existing
// Phase 116.2 claim-verification system (`useClaimCompany` /
// `ClaimVerificationPanel` / `company_claim_verifications`) reconciled
// 2026-07-09 — see onboarding-claim/index.ts's file header.
// ============================================================================

/** Exactly one of the two. `directoryId` is the verified directory claim
 * (`useClaimCompany`): the server re-checks the verification, links or creates
 * the page and marks the directory row claimed (118-10). */
export type ClaimCompanyRequest =
  | { startupId: string; directoryId?: never }
  | { directoryId: string; startupId?: never };

export interface ClaimCompanyResponse {
  success: boolean;
  /**
   * How the claim was authorized: 'directory_verified' (the startup is
   * linked to a `company_directory` row and the caller already holds a
   * verified `company_claim_verifications` row for it — obtained via
   * `ClaimVerificationPanel`), or 'unlinked_create' (no directory link, and
   * the page was created moments ago in the Create flow by the caller, so the
   * creating founder becomes owner outright).
   * (The former 'invite' method — an owner-role invite token — was removed
   * 2026-07-12; the path was unreachable. See onboarding-claim/index.ts B11.)
   */
  method: 'directory_verified' | 'unlinked_create';
  /** Always 'active' — every grant path is a hard proof now, no more provisional tier. */
  status: 'active';
  teamId: string;
  startupId: string;
}

/**
 * Assign `team_members` ownership for a company page (spec §2.2/§3). This
 * is a BRIDGE, not an independent verifier: for a startup linked to a
 * `company_directory` row, the caller must ALREADY hold a verified
 * `company_claim_verifications` row (`useClaimCompany` passes `directoryId`
 * and the server does the whole directory claim). For a startup with no
 * directory link, only the page the caller created moments ago in the
 * "Create" flow qualifies (403 `NOT_CLAIMABLE` otherwise). On a 409 conflict (another owner
 * already claimed it), `error` is "This company already has an active
 * owner claim." and `code` is `'ALREADY_OWNED'` — the caller should offer
 * Join instead.
 */
export async function claimCompany(
  params: ClaimCompanyRequest
): Promise<EdgeFunctionResponse<ClaimCompanyResponse>> {
  return callEdgeFunction<ClaimCompanyResponse>('onboarding-claim', params, {
    mockData: {
      success: true,
      method: 'unlinked_create',
      status: 'active',
      teamId: 'mock_team_id',
      startupId: params.startupId ?? 'mock_startup_id',
    },
  });
}

export interface JoinCompanyRequest {
  startupId: string;
  /** Set by the "Create" flow when a member creates a brand-new page — requests
   * ACTIVE membership. The server honours it only on a page created moments ago
   * that nobody else was ever part of (onboarding-join, 118-10); otherwise it
   * answers pending and writes nothing. */
  asCreator?: boolean;
}

export interface JoinCompanyResponse {
  success: boolean;
  /** 'active' only for the creator of a page made moments ago (or an existing
   * active member); anything else is 'pending' — join never grants owner. */
  status: 'active' | 'pending';
  /** null on a refusal: it never reveals whether the company has a team. */
  teamId: string | null;
  startupId: string;
}

/**
 * Request membership on an existing company page (spec §2.2 "Join" cells) —
 * writes a pending `team_members` row, never an instant grant. See
 * onboarding-join/index.ts for why this can't be a plain client-side insert
 * (RLS requires an existing owner/admin to write team_members).
 */
export async function joinCompany(
  params: JoinCompanyRequest
): Promise<EdgeFunctionResponse<JoinCompanyResponse>> {
  return callEdgeFunction<JoinCompanyResponse>('onboarding-join', params, {
    mockData: {
      success: true,
      // A member creating a brand-new page becomes its first active member;
      // a plain join is a pending request.
      status: params.asCreator ? 'active' : 'pending',
      teamId: 'mock_team_id',
      startupId: params.startupId,
    },
  });
}

// ============================================================================
// DD Functions
// ============================================================================

export interface DDInviteRequest {
  sharedDdId: string;
  email: string;
  role?: 'lead' | 'co-investor' | 'observer';
}

export interface DDInviteResponse {
  success: boolean;
  inviteId: string;
  expiresAt: string;
  token: string;
}

export async function sendDDInvite(
  params: DDInviteRequest
): Promise<EdgeFunctionResponse<DDInviteResponse>> {
  return callEdgeFunction<DDInviteResponse>('dd-invite', params, {
    mockData: {
      success: true,
      inviteId: `mock_dd_invite_${Date.now()}`,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      token: `mock_dd_token_${Date.now()}`,
    },
  });
}

export interface AcceptDDInviteRequest {
  token: string;
}

export interface AcceptDDInviteResponse {
  success: boolean;
  sharedDdId: string;
  startupName: string;
}

export async function acceptDDInvite(
  params: AcceptDDInviteRequest
): Promise<EdgeFunctionResponse<AcceptDDInviteResponse>> {
  return callEdgeFunction<AcceptDDInviteResponse>('dd-accept-invite', params, {
    mockData: {
      success: true,
      sharedDdId: 'mock_dd_id',
      startupName: 'Demo Startup',
    },
  });
}

// ============================================================================
// Document Functions
// ============================================================================

export interface SignedUrlRequest {
  documentId: string;
  expiresIn?: number;
}

export interface SignedUrlResponse {
  url: string;
  expiresAt: string;
}

export async function getDocumentSignedUrl(
  params: SignedUrlRequest
): Promise<EdgeFunctionResponse<SignedUrlResponse>> {
  return callEdgeFunction<SignedUrlResponse>('document-signed-url', params, {
    mockData: {
      url: `https://example.com/mock-document/${params.documentId}`,
      expiresAt: new Date(Date.now() + (params.expiresIn || 3600) * 1000).toISOString(),
    },
  });
}

export interface RecordDataRoomViewResponse {
  recorded: boolean;
}

/**
 * Record that the current investor opened a data-room document (FR-P0-07).
 * Fire-and-forget from the DD document open handler — the founder's real
 * per-document viewer stats read from the resulting data_room_events rows.
 * Owner self-views and mock/non-existent documents are no-ops server-side.
 */
export async function recordDataRoomView(
  documentId: string
): Promise<EdgeFunctionResponse<RecordDataRoomViewResponse>> {
  return callEdgeFunction<RecordDataRoomViewResponse>('record-data-room-view', { documentId }, {
    mockData: { recorded: false },
  });
}

// ============================================================================
// AI Functions
// ============================================================================

export interface PitchDeckExtractRequest {
  documentId: string;
}

export interface ExtractedField {
  value: string | number | string[];
  confidence: number;
}

export interface ExtractedStartupData {
  name: ExtractedField;
  tagline: ExtractedField;
  industry: ExtractedField;
  stage: ExtractedField;
  problem: ExtractedField;
  solution: ExtractedField;
  targetCustomer: ExtractedField;
  marketSize: ExtractedField;
  fundingTarget: ExtractedField;
  teamSize: ExtractedField;
  competitors: ExtractedField;
  location: ExtractedField;
  foundedYear: ExtractedField;
  monthlyRevenue: ExtractedField;
  userCount: ExtractedField;
  growthRate: ExtractedField;
  // Founder contact info for investor outreach
  founderName: ExtractedField;
  founderEmail: ExtractedField;
  founderLinkedIn: ExtractedField;
  website: ExtractedField;
  overallConfidence: number;
}

export interface PitchDeckExtractResponse {
  success: boolean;
  data: ExtractedStartupData;
  processingTimeMs: number;
}

export async function extractPitchDeck(
  params: PitchDeckExtractRequest
): Promise<EdgeFunctionResponse<PitchDeckExtractResponse>> {
  return callEdgeFunction<PitchDeckExtractResponse>('pitch-deck-extract', params, {
    timeout: 60_000,
    mockData: {
      success: true,
      data: {
        name: { value: 'Demo Startup', confidence: 85 },
        tagline: { value: 'Building the future', confidence: 75 },
        industry: { value: 'Technology', confidence: 80 },
        stage: { value: 'seed', confidence: 70 },
        problem: { value: 'Manual processes waste time and resources', confidence: 75 },
        solution: { value: 'AI-powered automation platform', confidence: 80 },
        targetCustomer: { value: 'Mid-market B2B companies', confidence: 70 },
        marketSize: { value: 5000000000, confidence: 65 },
        fundingTarget: { value: 2500000, confidence: 85 },
        teamSize: { value: 8, confidence: 90 },
        competitors: { value: ['Competitor A', 'Competitor B'], confidence: 60 },
        location: { value: 'Zurich, Switzerland', confidence: 90 },
        foundedYear: { value: 2023, confidence: 95 },
        monthlyRevenue: { value: 25000, confidence: 60 },
        userCount: { value: 150, confidence: 65 },
        growthRate: { value: 18, confidence: 55 },
        founderName: { value: 'Jane Smith', confidence: 85 },
        founderEmail: { value: 'jane@demo.com', confidence: 90 },
        founderLinkedIn: { value: 'https://linkedin.com/in/janesmith', confidence: 80 },
        website: { value: 'https://demo.com', confidence: 95 },
        overallConfidence: 72,
      },
      processingTimeMs: 2000,
    },
  });
}

export interface InvestorMatchRequest {
  startupId: string;
  investorIds?: string[];
  limit?: number;
}

export interface MatchFactors {
  industry: number;
  stage: number;
  ticketSize: number;
  location: number;
  activityLevel: number;
  profileComplete: number;
}

export interface InvestorMatch {
  investorId: string;
  investorName: string;
  firmName?: string;
  matchScore: number;
  factors: MatchFactors;
  breakdown: {
    industryMatch: string[];
    stageMatch: boolean;
    ticketFit: 'under' | 'within' | 'over';
    locationMatch: boolean;
  };
}

export interface InvestorMatchResponse {
  matches: InvestorMatch[];
  totalInvestors: number;
  startupName: string;
}

export async function getInvestorMatches(
  params: InvestorMatchRequest
): Promise<EdgeFunctionResponse<InvestorMatchResponse>> {
  return callEdgeFunction<InvestorMatchResponse>('investor-match', params, {
    dedupe: true,
    mockData: {
      matches: [],
      totalInvestors: 0,
      startupName: 'Demo Startup',
    },
  });
}

// ============================================================================
// Swiss Company Functions (Zefix)
// ============================================================================

export interface ZefixLookupRequest {
  cheNumber?: string;
  name?: string;
  canton?: string;
}

export interface ZefixCompanyData {
  cheNumber: string;
  name: string;
  legalForm: string;
  status: string;
  canton: string;
  city: string;
  address?: string;
  registrationDate?: string;
  deletionDate?: string;
  purpose?: string;
  capital?: number;
  uid?: string;
  registryUrl?: string;
}

export interface ZefixSearchResultData {
  cheNumber: string;
  name: string;
  legalForm: string;
  status: string;
  canton: string;
  city: string;
}

export interface ZefixLookupResponse {
  found: boolean;
  company?: ZefixCompanyData;
  results?: ZefixSearchResultData[];
  confidence: number;
  matchType: 'exact' | 'partial' | 'fuzzy' | 'none';
}

export async function lookupSwissCompany(
  params: ZefixLookupRequest
): Promise<EdgeFunctionResponse<ZefixLookupResponse>> {
  const mockData: ZefixLookupResponse = (params.cheNumber || params.name)
    ? {
        found: true,
        company: {
          cheNumber: 'CHE-123.456.789',
          name: params.name || 'Demo AG',
          legalForm: 'AG',
          status: 'active',
          canton: 'ZH',
          city: 'Zürich',
          address: 'Bahnhofstrasse 1, 8001 Zürich',
          registrationDate: '2020-01-15',
          purpose: 'Software development and consulting services',
          capital: 100000,
          uid: 'CHE123456789',
          registryUrl: 'https://www.zefix.ch/en/search/entity/list/firm/CHE123456789',
        },
        confidence: 95,
        matchType: 'exact',
      }
    : { found: false, confidence: 0, matchType: 'none' };

  return callEdgeFunction<ZefixLookupResponse>('zefix-lookup', params, { mockData });
}

// ============================================================================
// Swiss Startup Ecosystem Functions (Startupticker)
// ============================================================================

export interface StartuptickerEnrichRequest {
  name: string;
  location?: string;
  website?: string;
}

export interface StartuptickerFundingData {
  type: string;
  amountChf?: number;
  date: string;
  leadInvestors?: string[];
  investors?: string[];
  sourceUrl?: string;
  description?: string;
}

export interface StartuptickerNewsData {
  title: string;
  date: string;
  url: string;
  excerpt?: string;
  category?: string;
}

export interface StartuptickerProfileData {
  name: string;
  description?: string;
  sector?: string;
  location?: string;
  foundedYear?: number;
  website?: string;
  linkedIn?: string;
  totalFunding?: number;
  latestRound?: StartuptickerFundingData;
  fundingHistory?: StartuptickerFundingData[];
  recentNews?: StartuptickerNewsData[];
  profileUrl?: string;
}

export interface StartuptickerEnrichResponse {
  found: boolean;
  profile?: StartuptickerProfileData;
  confidence: number;
  matchType: 'exact' | 'partial' | 'fuzzy' | 'none';
  enrichedAt: string;
  source: 'startupticker';
}

export async function enrichFromStartupticker(
  params: StartuptickerEnrichRequest
): Promise<EdgeFunctionResponse<StartuptickerEnrichResponse>> {
  return callEdgeFunction<StartuptickerEnrichResponse>('startupticker-enrich', params, {
    mockData: {
      found: true,
      profile: {
        name: params.name,
        description: 'An innovative Swiss startup building the future of technology',
        sector: 'Technology',
        location: params.location || 'Zürich',
        foundedYear: 2022,
        website: params.website || 'https://example.ch',
        totalFunding: 2500000,
        latestRound: {
          type: 'seed',
          amountChf: 2500000,
          date: '2024-06-15',
          leadInvestors: ['Swiss Founders Fund'],
          description: 'Seed round to accelerate product development',
        },
        fundingHistory: [
          {
            type: 'seed',
            amountChf: 2500000,
            date: '2024-06-15',
            leadInvestors: ['Swiss Founders Fund'],
          },
          {
            type: 'pre-seed',
            amountChf: 500000,
            date: '2023-03-01',
            leadInvestors: ['Venture Kick'],
          },
        ],
        recentNews: [
          {
            title: `${params.name} raises CHF 2.5M seed round`,
            date: '2024-06-15',
            url: 'https://startupticker.ch/example',
            category: 'funding',
          },
        ],
        profileUrl: 'https://startupticker.ch/company/example',
      },
      confidence: 90,
      matchType: 'exact',
      enrichedAt: new Date().toISOString(),
      source: 'startupticker',
    },
  });
}

// ============================================================================
// Website Enrichment Functions
// ============================================================================

export interface WebsiteEnrichRequest {
  websiteUrl: string;
  startupName?: string;
}

export interface ExtractedWebsiteField<T> {
  value: T;
  confidence: number;
  source?: string;
}

export interface WebsiteProductInfo {
  stage?: ExtractedWebsiteField<string>;
  description?: ExtractedWebsiteField<string>;
  hasApi?: ExtractedWebsiteField<boolean>;
  hasMobileApp?: ExtractedWebsiteField<boolean>;
}

export interface WebsiteBusinessModel {
  type?: ExtractedWebsiteField<string>;
  pricingModel?: ExtractedWebsiteField<string>;
  startingPrice?: ExtractedWebsiteField<string>;
  targetSegment?: ExtractedWebsiteField<string>;
}

export interface WebsiteTraction {
  customerLogos?: ExtractedWebsiteField<number>;
  testimonialCount?: ExtractedWebsiteField<number>;
  integrationCount?: ExtractedWebsiteField<number>;
}

export interface WebsiteMarket {
  industry?: ExtractedWebsiteField<string>;
  competitors?: ExtractedWebsiteField<string[]>;
  differentiators?: ExtractedWebsiteField<string[]>;
}

export interface WebsiteTeam {
  teamSize?: ExtractedWebsiteField<number>;
  founders?: ExtractedWebsiteField<string[]>;
  isHiring?: ExtractedWebsiteField<boolean>;
}

export interface WebsiteEnrichmentData {
  websiteUrl: string;
  companyName?: ExtractedWebsiteField<string>;
  tagline?: ExtractedWebsiteField<string>;
  product?: WebsiteProductInfo;
  businessModel?: WebsiteBusinessModel;
  traction?: WebsiteTraction;
  market?: WebsiteMarket;
  team?: WebsiteTeam;
  overallConfidence: number;
  pagesScraped: string[];
  enrichedAt: string;
}

export interface WebsiteEnrichResponse {
  success: boolean;
  data?: WebsiteEnrichmentData;
  error?: string;
  processingTimeMs: number;
}

export async function enrichFromWebsite(
  params: WebsiteEnrichRequest
): Promise<EdgeFunctionResponse<WebsiteEnrichResponse>> {
  if (mode.isMock) {
    // Simulate processing delay for realistic UX
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }

  return callEdgeFunction<WebsiteEnrichResponse>('website-enrich', params, {
    timeout: 60_000,
    mockData: {
      success: true,
      data: {
        websiteUrl: params.websiteUrl,
        companyName: { value: params.startupName || 'Demo Company', confidence: 90 },
        tagline: { value: 'Building the future of innovation', confidence: 85 },
        product: {
          stage: { value: 'live', confidence: 85 },
          description: { value: 'An AI-powered platform for modern businesses', confidence: 80 },
          hasApi: { value: true, confidence: 75 },
          hasMobileApp: { value: false, confidence: 70 },
        },
        businessModel: {
          type: { value: 'saas', confidence: 80 },
          pricingModel: { value: 'freemium', confidence: 75 },
          startingPrice: { value: 'CHF 49/month', confidence: 70 },
          targetSegment: { value: 'SMB & Enterprise', confidence: 75 },
        },
        traction: {
          customerLogos: { value: 15, confidence: 85 },
          testimonialCount: { value: 8, confidence: 80 },
          integrationCount: { value: 12, confidence: 75 },
        },
        market: {
          industry: { value: 'Technology / SaaS', confidence: 85 },
          competitors: { value: ['Competitor A', 'Competitor B', 'Competitor C'], confidence: 65 },
          differentiators: { value: ['AI-powered automation', 'Swiss data privacy', '10x faster'], confidence: 70 },
        },
        team: {
          teamSize: { value: 18, confidence: 60 },
          founders: { value: ['Sarah Johnson', 'Michael Chen'], confidence: 75 },
          isHiring: { value: true, confidence: 85 },
        },
        overallConfidence: 76,
        pagesScraped: ['/', '/about', '/pricing'],
        enrichedAt: new Date().toISOString(),
      },
      processingTimeMs: 1500,
    },
  });
}

// ============================================================================
// AI Scoring Functions
// ============================================================================

export type AIScoringStage = 'pre-seed' | 'seed' | 'series-a' | 'series-b-plus' | 'pre-ipo';
export type AIEvidenceLevel = 'verified' | 'partially-verified' | 'unverified';
export type AIRiskLevel = 'none' | 'low' | 'medium' | 'high';
export type AIReadinessLevel = 'critical' | 'weak' | 'moderate' | 'strong' | 'excellent';

export type AIEvidenceSourceType = 'self-claim' | 'third-party-cited' | 'dataroom-verified';

export interface AIScoreInput {
  name: string;
  stage: AIScoringStage;
  industry?: string;
  profileData: Record<string, unknown>;
  pitchDeckContent?: string;
  documentsContent?: string[];
  /** Rich extraction data — takes precedence over profileData when present */
  richExtraction?: RichExtraction;
}

export interface AIScoreRequest {
  input: AIScoreInput;
  model?: string;
  includeSubScores?: boolean;
  // NOTE: `deckBase64` / `deckMimeType` were REMOVED (2026-07-30).
  //
  // They were never read: nothing under `supabase/functions/_shared/scoring/`
  // consumed them. Every scoring call therefore uploaded the entire pitch deck
  // a second time — after `investor-analyze-deck` had already uploaded and
  // extracted it — for a value the server discarded. Against this call's 90s
  // budget (the analyze call gets 150s) a 4.2 MB deck's second upload ran out of
  // time mid-flight in production, so the request never reached the gateway and
  // the deal surfaced unscored with no error.
  //
  // Scoring's input is the extraction (`input`), not the file. If full-document
  // scoring is ever wanted, pass a storage path — not megabytes of base64.
}

export interface AIScoreResponse {
  success: boolean;
  /**
   * Phase 116.6: backend returns ScoreEventReadModel directly (no adapter shim).
   * Consumers parse this shape; the legacy AIScoreResult type is retired.
   */
  data?: ScoreEventReadModel;
  error?: string;
}

/**
 * Get AI-powered investment readiness score for a startup.
 */
export async function getAIScore(
  request: AIScoreRequest
): Promise<EdgeFunctionResponse<AIScoreResponse>> {
  if (mode.isMock) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }

  return callEdgeFunction<AIScoreResponse>('ai-score', request, {
    timeout: 90_000,
    dedupe: true,
    mockData: { success: true, data: getMockScoreEvent(request.input) },
  });
}

// =============================================================================
// Phase 116.6 — ScoreEventReadModel exported as the canonical scoring response
// =============================================================================

export interface ScoreEventReadModel {
  scoreEventId: string;
  methodologyVersion: string;
  computedAt: string;
  composite: {
    finalScore: number;
    fourAxis: {
      ventureQuality: number;
      readiness: number;
      risk: number;
      evidenceQuality: number;
    };
    ventureLabel: 'critical' | 'weak' | 'moderate' | 'strong' | 'excellent';
    evidenceGrade: 'high' | 'moderate' | 'low';
    dealReadiness: 'not-ready' | 'developing' | 'nearly-ready' | 'ready';
  };
  dimensions: Array<{
    id: string;
    rawScore: number;
    evidenceLevel: 'verified' | 'partially-verified' | 'unverified';
    confidence: number;
    feedback?: string;
    /**
     * Investor view without a data-room grant only: claim-status counts for the
     * dimension (see `lib/scoring/backing-status.ts`).
     */
    claimsSummary?: ClaimsSummary;
    /** Per-sub-criterion scores + evidence (v2.1.0+; absent on stored v2.0.0 events). */
    subScores?: Array<{
      id: string;
      score: number;
      /** Always present on founder / full rows; behind a switch in the investor view. */
      rationale?: string;
      /**
       * Verbatim quotes with their source — founders, admins and investors WITH
       * a data-room grant. Absent in the no-grant investor view.
       */
      evidence?: Array<{ quote: string; source: string }>;
      /** No-grant investor view: how many citations back the sub-score. */
      evidenceSummary?: { citations: number };
      /** No-grant investor view: claim-status counts for the sub-criterion. */
      claimsSummary?: ClaimsSummary;
    }>;
  }>;
  risks: Array<{
    category: string;
    description: string;
    severity: 'high' | 'medium' | 'low';
  }>;
  startup: {
    id: string;
    name: string;
    stage: string | null;
    sector: string | null;
  };
  /**
   * Investor reads only (`investor_score_events`): false when the caller holds
   * no data-room grant, so quotes, sources and claim text were withheld and the
   * UI offers a data-room request instead. Absent on founder reads (full row).
   */
  rawInputsVisible?: boolean;
}

/**
 * Mock score event for demo / mock-mode UX. Phase 116.6 — lean shape.
 */
function getMockScoreEvent(input: AIScoreInput): ScoreEventReadModel {
  const baseScore = 65 + Math.floor(Math.random() * 15);
  const ventureLabel: ScoreEventReadModel['composite']['ventureLabel'] =
    baseScore >= 81 ? 'excellent'
      : baseScore >= 66 ? 'strong'
      : baseScore >= 46 ? 'moderate'
      : baseScore >= 26 ? 'weak'
      : 'critical';
  const dimensionIds = [
    'team', 'product', 'market', 'traction', 'defensibility', 'competition',
    'business-model', 'timing', 'vision-story', 'gtm-execution',
    'financial-health', 'execution-velocity', 'scalability',
    'deal-attractiveness', 'social-proof',
  ];

  return {
    scoreEventId: `mock-${Date.now()}`,
    methodologyVersion: 'v2.0.0',
    computedAt: new Date().toISOString(),
    composite: {
      finalScore: baseScore,
      fourAxis: {
        ventureQuality: baseScore,
        readiness: Math.max(0, baseScore - 5),
        risk: 80,
        evidenceQuality: 65,
      },
      ventureLabel,
      evidenceGrade: 'moderate',
      dealReadiness: baseScore >= 66 ? 'nearly-ready' : 'developing',
    },
    dimensions: dimensionIds.map((id, i) => ({
      id,
      rawScore: 55 + ((baseScore + i * 3) % 30),
      evidenceLevel: (i % 3 === 0 ? 'verified' : i % 3 === 1 ? 'partially-verified' : 'unverified') as 'verified' | 'partially-verified' | 'unverified',
      confidence: 70 + (i % 20),
      feedback: `Mock feedback for ${id}.`,
    })),
    risks: [
      { category: 'market', description: 'Competition from incumbents', severity: 'medium' as const },
      { category: 'execution', description: 'Aggressive timeline', severity: 'low' as const },
    ],
    startup: {
      id: 'mock-startup-id',
      name: input.name,
      stage: input.stage,
      sector: input.industry ?? null,
    },
  };
}

// ============================================================================
// DD Chat Functions
// ============================================================================

export type DDChatRole = 'user' | 'assistant' | 'system';

export interface DDChatMessage {
  id: string;
  role: DDChatRole;
  content: string;
  timestamp: number;
  metadata?: {
    tokens?: number;
    processingTime?: number;
    sources?: string[];
  };
}

export interface DDStartupContext {
  name: string;
  stage?: string;
  industry?: string;
  location?: string;
  foundedYear?: number;
  website?: string;
  teamSize?: number;
  isSerialFounder?: boolean;
  previousVentures?: string;
  tagline?: string;
  description?: string;
  problem?: string;
  solution?: string;
  targetCustomer?: string;
  monthlyRevenue?: number;
  userCount?: number;
  growthRate?: number;
  marketSize?: string;
  competitors?: string;
  fundingTarget?: number;
  minimumTicket?: number;
  isActivelyRaising?: boolean;
  fundraisingHistory?: string;
  scores?: {
    finalScore?: number;
    readinessLevel?: string;
    dimensions?: Array<{
      dimension: string;
      name: string;
      score: number;
      feedback?: string;
    }>;
    topRisks?: string[];
    topStrengths?: string[];
  };
  swissVerification?: {
    isSwissStartup?: boolean;
    registrationStatus?: string;
  };
  startuptickerData?: {
    featured?: boolean;
    category?: string;
  };
  websiteEnrichment?: {
    techStack?: string[];
    socialLinks?: Record<string, string>;
  };
  dataRoomConnected?: boolean;
  dataRoomProvider?: string;
}

export interface DDChatRequest {
  message: string;
  history: DDChatMessage[];
  /**
   * B2: the basic dd-chat server rebuilds startup context server-side from
   * `startupId` (and the S4 data-room gate keys on it). Prefer passing this.
   */
  startupId?: string;
  /**
   * Client-side rendering context (mock mode + UI). B2/SEC-DOC-03: this is a
   * server-controlled field and is STRIPPED before the request reaches the real
   * edge function (see sendDDChatMessage) — it must never be sent to the server.
   */
  startupContext: DDStartupContext;
  investorPreferences?: {
    focusAreas?: string[];
    riskTolerance?: 'low' | 'medium' | 'high';
    investmentStage?: string[];
  };
}

export interface DDChatResponse {
  success: boolean;
  data?: {
    message: DDChatMessage;
    suggestedQuestions?: string[];
    topicsDiscussed?: string[];
  };
  error?: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

/**
 * Send a message to the DD Chat assistant
 */
export async function sendDDChatMessage(
  request: DDChatRequest
): Promise<EdgeFunctionResponse<DDChatResponse>> {
  if (mode.isMock) {
    // Simulate processing delay for realistic UX
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }

  // B2/SEC-DOC-03: `startupContext` is a client-controlled field the server rejects
  // with 400 (it rebuilds context from startupId). Strip it from the real request
  // body; keep the full request for mock rendering only.
  const { startupContext: _omit, ...serverBody } = request;
  return callEdgeFunction<DDChatResponse>('dd-chat', serverBody, {
    timeout: 90_000,
    mockData: { success: true, data: getMockDDChatResponse(request) },
  });
}

/**
 * Generate mock DD Chat response for demo mode
 */
function getMockDDChatResponse(request: DDChatRequest): DDChatResponse['data'] {
  const { message, startupContext } = request;
  const lowercaseMessage = message.toLowerCase();

  // Generate contextual response based on question type
  let responseContent: string;
  let topics: string[] = [];

  if (lowercaseMessage.includes('risk') || lowercaseMessage.includes('concern')) {
    topics = ['risks'];
    responseContent = `## Key Risks for ${startupContext.name}

Based on the available data, here are the main investment risks to consider:

**Team Risks**
${startupContext.teamSize && startupContext.teamSize < 5
  ? '- Small team size may limit execution capacity\n'
  : ''}${!startupContext.isSerialFounder
  ? '- First-time founders — consider mentorship and advisory support\n'
  : ''}

**Market Risks**
${startupContext.marketSize
  ? `- Market sizing (${startupContext.marketSize}) needs validation\n`
  : '- Market size not clearly defined\n'}
${startupContext.competitors
  ? `- Competitive landscape includes: ${startupContext.competitors}\n`
  : '- Competitive analysis not documented\n'}

**Traction Risks**
${!startupContext.monthlyRevenue
  ? '- Revenue metrics not disclosed\n'
  : startupContext.monthlyRevenue < 10000
    ? '- Early revenue stage — unit economics unproven\n'
    : ''}
${!startupContext.growthRate
  ? '- Growth rate not documented\n'
  : ''}

Would you like me to dive deeper into any of these risk areas?`;
  } else if (lowercaseMessage.includes('team') || lowercaseMessage.includes('founder')) {
    topics = ['team'];
    responseContent = `## Team Analysis for ${startupContext.name}

**Team Composition**
- Team Size: ${startupContext.teamSize || 'Not specified'}
- Serial Founder: ${startupContext.isSerialFounder ? 'Yes' : 'No / Not specified'}
${startupContext.previousVentures ? `- Previous Ventures: ${startupContext.previousVentures}\n` : ''}

**Assessment**
${startupContext.isSerialFounder
  ? 'The presence of a serial founder is a positive signal — they bring valuable experience in building and scaling startups.'
  : 'First-time founders can succeed but may benefit from experienced advisors or board members.'}

${startupContext.teamSize
  ? startupContext.teamSize >= 5
    ? 'The team size suggests they have core functions covered.'
    : 'The lean team may need to expand as they scale.'
  : 'Team composition details would help assess execution capability.'}

What specific aspects of the team would you like to explore further?`;
  } else if (lowercaseMessage.includes('traction') || lowercaseMessage.includes('revenue') || lowercaseMessage.includes('growth')) {
    topics = ['traction'];
    responseContent = `## Traction Analysis for ${startupContext.name}

**Key Metrics**
- Monthly Revenue: ${startupContext.monthlyRevenue ? `$${startupContext.monthlyRevenue.toLocaleString()}` : 'Not disclosed'}
- Users: ${startupContext.userCount ? startupContext.userCount.toLocaleString() : 'Not disclosed'}
- Growth Rate: ${startupContext.growthRate ? `${startupContext.growthRate}%` : 'Not disclosed'}

**Stage Context**
At the ${startupContext.stage || 'current'} stage, investors typically look for:
${startupContext.stage === 'pre-seed' || startupContext.stage === 'seed'
  ? '- Early signs of product-market fit\n- User engagement and retention signals\n- Initial customer feedback'
  : '- Consistent month-over-month growth\n- Clear unit economics\n- Repeatable customer acquisition'}

${startupContext.monthlyRevenue && startupContext.monthlyRevenue > 0
  ? `\n**Revenue Analysis**\nWith $${startupContext.monthlyRevenue.toLocaleString()} MRR, the startup shows ${startupContext.monthlyRevenue > 50000 ? 'meaningful' : 'early'} revenue traction.`
  : '\n**Note**: Revenue data would help assess commercial viability.'}

Would you like me to analyze the traction relative to their funding ask?`;
  } else if (lowercaseMessage.includes('market') || lowercaseMessage.includes('competition') || lowercaseMessage.includes('competitor')) {
    topics = ['market', 'competition'];
    responseContent = `## Market Analysis for ${startupContext.name}

**Market Opportunity**
- Industry: ${startupContext.industry || 'Not specified'}
- Market Size: ${startupContext.marketSize || 'Not specified'}
- Target Customer: ${startupContext.targetCustomer || 'Not specified'}

**Competitive Landscape**
${startupContext.competitors
  ? `Known competitors: ${startupContext.competitors}\n\nUnderstanding how ${startupContext.name} differentiates from these players is crucial for assessing their competitive moat.`
  : 'Competitive analysis not documented. Consider asking the founders about their key competitors and differentiation strategy.'}

**Questions to Consider**
1. How defensible is the market position?
2. What is the path to market leadership?
3. Are there regulatory or geographic considerations?

Shall I explore any of these areas in more detail?`;
  } else {
    // General overview
    topics = ['general'];
    responseContent = `## Overview of ${startupContext.name}

**Company Snapshot**
- Stage: ${startupContext.stage || 'Not specified'}
- Industry: ${startupContext.industry || 'Not specified'}
- Location: ${startupContext.location || 'Not specified'}
${startupContext.foundedYear ? `- Founded: ${startupContext.foundedYear}` : ''}

**Value Proposition**
${startupContext.tagline || startupContext.description || 'No description provided.'}

${startupContext.problem ? `**Problem**: ${startupContext.problem}\n` : ''}
${startupContext.solution ? `**Solution**: ${startupContext.solution}\n` : ''}

**Investment Status**
${startupContext.isActivelyRaising
  ? `Currently raising ${startupContext.fundingTarget ? `$${startupContext.fundingTarget.toLocaleString()}` : 'an undisclosed amount'}`
  : 'Not currently raising / Status unknown'}
${startupContext.minimumTicket ? `\nMinimum ticket: $${startupContext.minimumTicket.toLocaleString()}` : ''}

${startupContext.scores?.finalScore
  ? `\n**Inverio Score Rating**: ${startupContext.scores.finalScore}/100 (${startupContext.scores.readinessLevel || 'Assessed'})`
  : ''}

What aspect would you like to explore? I can analyze the team, traction, market, risks, or funding terms.`;
  }

  return {
    message: {
      id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      role: 'assistant',
      content: responseContent,
      timestamp: Date.now(),
      metadata: {
        tokens: 500,
        processingTime: 1500,
        sources: topics,
      },
    },
    suggestedQuestions: [
      'What are the main risks I should consider?',
      'How strong is the founding team?',
      'Is the traction compelling for this stage?',
      'How does the valuation compare to peers?',
    ].filter(q => !lowercaseMessage.includes(q.toLowerCase().split(' ')[3] || '')).slice(0, 3),
    topicsDiscussed: topics,
  };
}

// ============================================================================
// Scoring Interview Functions (Phase 87 engine → 88 founder UI)
// ============================================================================
//
// Typed wrappers over the founder-only `interview-agent` edge function. Mirrors
// `sendDDChatMessage` exactly: `callEdgeFunction` owns the `mode.useRealAPI`
// guard, auth, timeout, and error shape. Each wrapper posts `{ action, ... }`
// matching `interviewAgentSchema` (strict discriminated union) server-side.
//
// TRADE-SECRET NOTE: the response types (see `@/lib/interview/types`) carry only
// founder-friendly labels + transcript — never dimension/sub-criterion ids or
// weights. Do not add fields here that would leak methodology internals.

/**
 * Start (or resume) a scoring interview for a startup the founder owns. Server
 * runs gap analysis, pins the "before" score_event, and returns a deterministic
 * opener (or the full transcript when resuming an active session).
 */
export async function startInterview(
  params: { startupId: string },
): Promise<EdgeFunctionResponse<InterviewStartResponse>> {
  return callEdgeFunction<InterviewStartResponse>(
    'interview-agent',
    { action: 'start', startupId: params.startupId },
    { timeout: 90_000 },
  );
}

/**
 * Send one founder turn. Server persists the answer, runs the curious-mentor
 * LLM turn, and returns the assistant `reply`. Voice transcripts (Phase 88b
 * Voxtral) flow through this same action.
 */
export async function sendInterviewMessage(
  params: { sessionId: string; content: string },
): Promise<EdgeFunctionResponse<InterviewMessageResponse>> {
  return callEdgeFunction<InterviewMessageResponse>(
    'interview-agent',
    { action: 'message', sessionId: params.sessionId, content: params.content },
    { timeout: 90_000 },
  );
}

/**
 * Conclude the session. Returns immediately with `rescoreStatus:'processing'` —
 * the evidence-driven re-score runs in the background and pins
 * `score_event_after` itself. The after-score is NEVER in this response; poll
 * the session row (`useInterviewSession`) to reveal the before/after uplift.
 */
export async function completeInterview(
  params: { sessionId: string },
): Promise<EdgeFunctionResponse<InterviewCompleteResponse>> {
  return callEdgeFunction<InterviewCompleteResponse>(
    'interview-agent',
    { action: 'complete', sessionId: params.sessionId },
    { timeout: 30_000 },
  );
}

/**
 * Build the factual company digest for a session (wrap-up review / re-open).
 * Server loads the transcript + extractions and returns a verbatim-quote-or-drop
 * digest: prose recap, About draft, and allowlist-only public-safe field
 * proposals. Nothing is published — publishing is a separate, founder-approved
 * step (see `publishPublicSafeFields`). The digest is persisted server-side so
 * it can be re-opened later.
 */
export async function generateDigest(
  params: { sessionId: string },
): Promise<EdgeFunctionResponse<InterviewDigestResponse>> {
  return callEdgeFunction<InterviewDigestResponse>(
    'interview-agent',
    { action: 'digest', sessionId: params.sessionId },
    { timeout: 30_000 },
  );
}

// ============================================================================
// Startup Insights Functions
// ============================================================================

export type InsightsRole = 'user' | 'assistant' | 'system';

export type InsightsFocusArea =
  | 'score-improvement'
  | 'gap-analysis'
  | 'investor-prep'
  | 'pitch-practice'
  | 'market-positioning'
  | 'team-building'
  | 'traction-growth'
  | 'fundraising'
  | 'general';

export interface InsightsMessage {
  id: string;
  role: InsightsRole;
  content: string;
  timestamp: number;
  metadata?: {
    tokens?: number;
    processingTime?: number;
    topics?: string[];
    actionItems?: string[];
  };
}

export interface InsightsStartupContext {
  name: string;
  stage?: string;
  industry?: string;
  location?: string;
  foundedYear?: number;
  website?: string;
  teamSize?: number;
  isSerialFounder?: boolean;
  previousVentures?: string;
  tagline?: string;
  description?: string;
  problem?: string;
  solution?: string;
  targetCustomer?: string;
  monthlyRevenue?: number;
  userCount?: number;
  growthRate?: number;
  marketSize?: string;
  competitors?: string;
  fundingTarget?: number;
  minimumTicket?: number;
  isActivelyRaising?: boolean;
  fundraisingHistory?: string;
  scores?: {
    finalScore?: number;
    readinessLevel?: string;
    dimensions?: Array<{
      dimension: string;
      name: string;
      score: number;
      feedback?: string;
      strength?: string;
      weakness?: string;
    }>;
    topRisks?: string[];
    topStrengths?: string[];
    recommendations?: string[];
  };
  dataRoomConnected?: boolean;
  dataRoomProvider?: string;
  swissVerification?: {
    isSwissStartup?: boolean;
    registrationStatus?: string;
  };
  startuptickerData?: {
    featured?: boolean;
    category?: string;
  };
}

export interface InsightsActionItem {
  id: string;
  action: string;
  priority: 'high' | 'medium' | 'low';
  dimension?: string;
  potentialImpact?: string;
  timeframe?: string;
}

export interface InsightsRequest {
  message: string;
  history: InsightsMessage[];
  startupContext: InsightsStartupContext;
  focusArea?: InsightsFocusArea;
}

export interface InsightsResponse {
  success: boolean;
  data?: {
    message: InsightsMessage;
    suggestedQuestions?: string[];
    actionItems?: InsightsActionItem[];
    topicsDiscussed?: string[];
  };
  error?: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

/**
 * Send a message to the Startup Insights assistant
 */
export async function sendInsightsMessage(
  request: InsightsRequest
): Promise<EdgeFunctionResponse<InsightsResponse>> {
  if (mode.isMock) {
    // Simulate processing delay for realistic UX
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }

  return callEdgeFunction<InsightsResponse>('startup-insights', request, {
    timeout: 90_000,
    mockData: { success: true, data: getMockInsightsResponse(request) },
  });
}

// ============================================================================
// Investor Pitch Deck Analysis Functions
// ============================================================================

export interface InvestorAnalyzeDeckRequest {
  /**
   * Opaque, client-minted id. Also the storage key segment, so a retry reuses
   * the object already uploaded instead of orphaning a second copy.
   *
   * This is the ONLY part of the deck's location we send. The bucket is a
   * server constant and the tenant segment is derived from the authenticated
   * session, because the function reads under the service role — which bypasses
   * RLS. Sending a path would let an investor name a founder's `pitch-decks`
   * object and have it extracted for them. See `@/lib/investor/deck-upload`.
   */
  analysisId: string;
  fileName: string;
  mimeType: string;
  /**
   * 119-03 / 119-07: the investor confirmed this deck is NOT the closed company
   * it matched. Only meaningful on a retry of an analysis that stopped with
   * `failure_reason = 'matched_closed_company'` (same analysisId); the server
   * re-opens only that job. Must be a boolean (400 otherwise).
   */
  confirm_no_conflict?: boolean;
}

// ============================================================================
// Rich Extraction Types (client-side mirror of server types)
// ============================================================================

export interface RichExtractionSlide {
  index: number;
  type: string;
  summary: string;
  visualElements: string[];
}

export interface RichExtractionMetric {
  name: string;
  value: number;
  unit: string;
  source: string;
  period?: string;
  confidence: number;
}

export interface RichExtractionChart {
  chartTitle: string;
  chartType: string;
  dataPoints: Array<{ label: string; value: number | string }>;
  sourceIndex: number;
}

export interface RichExtractionPerson {
  name: string;
  role: string;
  relationship: string;
  linkedIn?: string;
  email?: string;
  background?: string;
}

export interface RichExtractionCompany {
  name: string;
  relationship: string;
  industry?: string;
  url?: string;
  description?: string;
}

export interface RichExtractionFundingRound {
  roundType: string;
  amount?: number;
  currency?: string;
  status: string;
  leadInvestor?: string;
  date?: string;
}

export interface RichExtractionQuality {
  overallConfidence: number;
  sectionConfidences: Array<{ section: string; confidence: number; sources: string[] }>;
  gapsIdentified: Array<{ description: string; severity: string; section: string }>;
  dataCompleteness: number;
  passesSucceeded: number;
  passesAttempted: number;
}

export interface RichExtraction {
  structure: {
    totalPages: number;
    slides: RichExtractionSlide[];
    narrativeFlow: string;
    designQuality: string;
    missingSections: string[];
    structureScore: number;
  };
  data: {
    metrics: RichExtractionMetric[];
    charts: RichExtractionChart[];
    financialProjections: Array<{ label: string; value: number; unit: string; period: string; basis: string }>;
    unitEconomics: Record<string, number | string | undefined>;
    marketData: Record<string, number | string | undefined>;
    dataRichnessScore: number;
  };
  entities: {
    people: RichExtractionPerson[];
    companies: RichExtractionCompany[];
    products: Array<{ name: string; type: string; description?: string }>;
    locations: string[];
    fundingRounds: RichExtractionFundingRound[];
    goToMarket: {
      channels: string[];
      businessModel?: string;
      pricingModel?: string;
      distributionSummary?: string;
    };
  };
  quality: RichExtractionQuality;
  metadata: {
    documentType: string;
    fileName: string;
    totalDurationMs: number;
    passDurations: Record<string, number>;
    modelsUsed: Record<string, string>;
    totalUsage: { inputTokens: number; outputTokens: number };
    pipelineVersion: string;
  };
  companyName: string;
  companyTagline: string;
  industry: string;
  stage: string;
}

// ── StartupSummary (investor dealflow path — replaces StartupProfile for deck-analyzed startups) ──

export interface StartupSummary {
  id: string;
  company: {
    name: string;
    tagline: string;
    description: string;
    industry: string;
    stage: string;
    location: string;
    website: string;
    founded: number;
    /**
     * Company logo, re-hosted in the public `company-logos` bucket.
     *
     * Lives canonically on `company_directory.logo_url` and is JOINED in rather
     * than copied onto `startup_profiles`: a second column would be a second
     * source of truth to keep in sync, which is the failure mode this codebase
     * keeps hitting. The 1:1 pairing that makes the join safe is enforced by
     * `company_directory_startup_profile_uniq`.
     *
     * `undefined` means "not loaded on this path", `null` means "no logo" —
     * both render the initials fallback.
     */
    logoUrl?: string | null;
    /**
     * The logo is a near-white knockout and needs a dark chip behind it.
     * A property of the logo, so it travels with it — a surface holding the URL
     * without this renders white-on-teal and shows nothing.
     */
    logoIsKnockout?: boolean;
  };
  team: {
    people: Array<{
      name: string;
      role: string;
      relationship: string;
      background?: string;
      linkedIn?: string;
      email?: string;
    }>;
    size: number;
    compositionSummary: string;
  };
  problemSolution: { problem: string; solution: string };
  market: {
    tam: number;
    sam: number;
    som: number;
    tamSource: string;
    growthRate: number;
    competitors: Array<{ name: string; relationship: string; description?: string }>;
  };
  traction: {
    metrics: Array<{ name: string; value: number; unit: string; period?: string }>;
  };
  financials: {
    unitEconomics: Record<string, number | string | undefined>;
    projections: Array<{ label: string; value: number; unit: string; period: string }>;
  };
  funding: {
    currentRaise: { amount: number; currency: string } | null;
    history: Array<{
      roundType: string;
      amount: number;
      currency: string;
      status: string;
      leadInvestor?: string;
      date?: string;
    }>;
    isActivelyRaising: boolean;
  };
  product: Array<{ name: string; type: string; description?: string }>;
  gtm: {
    channels: string[];
    businessModel: string;
    pricingModel: string;
    distributionSummary: string;
  };
  socialProof: {
    investorNames: string[];
    partnerships: string[];
    awards: string[];
  };
  deckQuality: {
    structureScore: number;
    narrativeFlow: string;
    designQuality: string;
    completeness: number;
    missingSections: string[];
  };
}

export interface InvestorAnalyzeDeckResponse {
  success: boolean;
  /**
   * The analysis this call started. Everything else about it — company,
   * score, pipeline card, failure reason — arrives via `deck_analyses`.
   *
   * The endpoint now answers 202 as soon as the job is RECORDED, and runs
   * extraction/promotion/scoring in a background task. It used to do all of it
   * inline and answer once: one production run took 46,818 ms, an 8.7 MB deck
   * blew the client's 150s budget, and an 18.5 MB deck killed the isolate with
   * no error at all. There is therefore no longer a result to return here.
   */
  analysisId?: string;
  /** Always 'analysing' on acceptance; later states are read from the table. */
  status?: 'analysing';
  error?: string;
}

/**
 * Analyze a pitch deck uploaded by an investor
 * Extracts startup data and calculates a preliminary investment readiness score
 */
export async function analyzeInvestorPitchDeck(
  request: InvestorAnalyzeDeckRequest
): Promise<EdgeFunctionResponse<InvestorAnalyzeDeckResponse>> {
  if (!mode.useRealAPI) {
    // Simulate processing delay for realistic UX (mock mode only)
    await new Promise((resolve) => setTimeout(resolve, 3000));
  }

  // 30s, down from 150s. The request is now a few hundred bytes and the server
  // answers as soon as it has RECORDED the job — it no longer waits for
  // extraction, promotion or scoring. A budget sized for the old inline
  // pipeline would just be a slow way to fail.
  return callEdgeFunction<InvestorAnalyzeDeckResponse>('investor-analyze-deck', request, {
    timeout: 30_000,
    dedupe: true,
    mockData: getMockInvestorAnalyzeResponse(request.analysisId),
  });
}

/**
 * Generate mock response for demo mode
 */
function getMockInvestorAnalyzeResponse(analysisId: string): InvestorAnalyzeDeckResponse {
  // Mock mode mirrors the real contract: an acknowledgement, not a result.
  // The rich mock payload this used to return described a response shape that
  // no longer exists, which would have let mock mode pass while production
  // renders nothing.
  return { success: true, analysisId, status: 'analysing' };
}

/**
 * Generate mock Startup Insights response for demo mode
 */
function getMockInsightsResponse(request: InsightsRequest): InsightsResponse['data'] {
  const { message, startupContext } = request;
  const lowercaseMessage = message.toLowerCase();

  let responseContent: string;
  let topics: string[] = [];
  let actionItems: InsightsActionItem[] = [];

  // Calculate mock gaps if scores available
  const lowestDimension = startupContext.scores?.dimensions?.reduce((prev, curr) =>
    curr.score < prev.score ? curr : prev
  );

  if (lowercaseMessage.includes('score') || lowercaseMessage.includes('improve')) {
    topics = ['score'];
    const finalScore = startupContext.scores?.finalScore || 60;

    responseContent = `## Score Improvement Strategy for ${startupContext.name}

**Current Position**
Your Inverio Score is **${finalScore}/100** (${startupContext.scores?.readinessLevel || 'Moderate'}). Here's how to move up:

**Priority Improvements**

${lowestDimension ? `1. **${lowestDimension.name}** (${lowestDimension.score}/100)
   This is your biggest opportunity. ${lowestDimension.weakness || 'Focus on strengthening this area.'}
   - Document any evidence you have but haven't submitted
   - Consider what quick wins could improve this score
   - Aim for +15 points here for maximum impact

` : ''}2. **Evidence Level**
   Moving from "self-declared" to "documented" evidence can boost scores by 10-15%:
   - Upload financial statements to verify revenue claims
   - Add customer testimonials or case studies
   - Connect your data room for automatic verification

3. **Quick Wins**
   - Complete any missing profile fields
   - Add team LinkedIn profiles
   - Document your competitive advantages

**Realistic Timeline**
- Week 1-2: Documentation improvements (+5-10 points)
- Month 1: Address lowest dimension (+10-15 points)
- Month 2-3: Build verified traction data (+5-10 points)

What specific dimension would you like to focus on improving first?`;

    actionItems = [
      { id: `action_${Date.now()}_1`, action: 'Upload financial documentation to verify revenue claims', priority: 'high' },
      { id: `action_${Date.now()}_2`, action: 'Complete missing profile fields', priority: 'high' },
      { id: `action_${Date.now()}_3`, action: `Address ${lowestDimension?.name || 'lowest scoring dimension'} gaps`, priority: 'high' },
      { id: `action_${Date.now()}_4`, action: 'Add team LinkedIn profiles for credibility', priority: 'medium' },
    ];

  } else if (lowercaseMessage.includes('investor') || lowercaseMessage.includes('pitch') || lowercaseMessage.includes('question')) {
    topics = ['fundraising'];
    responseContent = `## Investor Preparation for ${startupContext.name}

Based on your profile, here are the tough questions investors will likely ask:

**Questions to Prepare For**

1. **On Team** ${!startupContext.isSerialFounder ? '(Your gap area)' : ''}
   "What makes your team uniquely qualified to solve this problem?"
   ${!startupContext.isSerialFounder ? '→ Prepare: Highlight relevant domain expertise, not just startup experience' : ''}

2. **On Traction** ${!startupContext.monthlyRevenue ? '(Missing data)' : ''}
   "Walk me through your unit economics"
   ${!startupContext.monthlyRevenue ? '→ Prepare: Have CAC, LTV, and payback period ready even if early' : ''}

3. **On Market**
   "How big can this really get?"
   → Prepare: Bottom-up TAM calculation, not just top-down industry size

4. **On Competition**
   "Why won't [big incumbent] just copy this?"
   → Prepare: Your defensibility story - what creates lasting competitive advantage

**Your Strengths to Emphasize**
${startupContext.scores?.topStrengths?.map(s => `- ${s}`).join('\n') || '- Focus on what you do better than anyone else'}

**Objections to Get Ahead Of**
${startupContext.scores?.topRisks?.map(r => `- ${r}`).join('\n') || '- Address obvious concerns before they\'re raised'}

Would you like to practice answering any of these questions?`;

    actionItems = [
      { id: `action_${Date.now()}_1`, action: 'Prepare unit economics breakdown (CAC, LTV, payback)', priority: 'high' },
      { id: `action_${Date.now()}_2`, action: 'Build bottom-up TAM calculation', priority: 'high' },
      { id: `action_${Date.now()}_3`, action: 'Document competitive moat and defensibility', priority: 'medium' },
    ];

  } else if (lowercaseMessage.includes('gap') || lowercaseMessage.includes('weak') || lowercaseMessage.includes('miss')) {
    topics = ['score', 'gap-analysis'];
    responseContent = `## Gap Analysis for ${startupContext.name}

**Critical Gaps to Address**

${startupContext.scores?.dimensions
  ?.filter(d => d.score < 60)
  .map((d, i) => `${i + 1}. **${d.name}** — Score: ${d.score}/100
   Gap: ${d.weakness || 'Below investment threshold'}
   Impact: ${d.score < 40 ? 'Critical blocker for investors' : 'Significant concern'}
   Action: ${d.feedback || 'Focus on documenting evidence and building track record'}`)
  .join('\n\n') || 'No critical gaps identified. Focus on maintaining strengths.'}

**Evidence Gaps**
${!startupContext.dataRoomConnected ? '- ⚠️ No data room connected — investors will want to verify claims' : '✓ Data room connected'}
${!startupContext.monthlyRevenue ? '- ⚠️ Revenue not disclosed — adds uncertainty for investors' : `✓ Revenue: $${startupContext.monthlyRevenue?.toLocaleString()}/mo`}
${!startupContext.growthRate ? '- ⚠️ Growth rate not documented — hard to assess momentum' : `✓ Growth: ${startupContext.growthRate}%`}

**Stage-Appropriate Expectations**
For a ${startupContext.stage || 'seed'} stage startup, investors typically expect:
${startupContext.stage === 'pre-seed'
  ? '- Clear problem/solution fit\n- Founding team with relevant experience\n- Early customer validation'
  : startupContext.stage === 'seed'
    ? '- Product-market fit signals\n- Early revenue or strong engagement\n- Repeatable customer acquisition'
    : '- Strong revenue growth (3x+ YoY)\n- Clear unit economics\n- Scalable go-to-market'}

Which gap would you like to address first?`;

    actionItems = [
      { id: `action_${Date.now()}_1`, action: 'Connect data room for verification', priority: !startupContext.dataRoomConnected ? 'high' : 'low' },
      { id: `action_${Date.now()}_2`, action: 'Document missing metrics (revenue, growth)', priority: 'high' },
      { id: `action_${Date.now()}_3`, action: `Address ${lowestDimension?.name || 'lowest dimension'} weaknesses`, priority: 'high' },
    ];

  } else {
    // General overview
    topics = ['general'];
    const finalScore = startupContext.scores?.finalScore || 60;
    const readiness = startupContext.scores?.readinessLevel || 'moderate';

    responseContent = `## Investment Readiness Overview for ${startupContext.name}

**Current Status**
- Inverio Score: **${finalScore}/100**
- Readiness: **${readiness.charAt(0).toUpperCase() + readiness.slice(1)}**
- Stage: ${startupContext.stage || 'Not specified'}
${startupContext.isActivelyRaising ? `- Raising: $${startupContext.fundingTarget?.toLocaleString() || 'Amount not specified'}` : ''}

**What's Working** ✓
${startupContext.scores?.topStrengths?.slice(0, 3).map(s => `- ${s}`).join('\n') || '- Review your scores to identify strengths'}

**Priority Improvements** ⚠️
${startupContext.scores?.topRisks?.slice(0, 3).map(r => `- ${r}`).join('\n') || '- Complete your profile for detailed recommendations'}

**Recommended Next Steps**
1. ${finalScore < 50 ? 'Focus on critical gaps before approaching investors' : finalScore < 70 ? 'Address top 2-3 weaknesses to reach "investor-ready"' : 'Fine-tune positioning and prepare for investor conversations'}
2. ${!startupContext.dataRoomConnected ? 'Connect your data room for verification' : 'Keep data room updated with latest metrics'}
3. ${!startupContext.monthlyRevenue ? 'Document your traction metrics' : 'Track and document growth trajectory'}

How can I help you improve your investment readiness today? I can help with:
- **Score improvement** — specific tips to boost your rating
- **Gap analysis** — identify what's holding you back
- **Investor prep** — practice tough questions
- **Documentation** — what to include in your data room`;

    actionItems = [
      { id: `action_${Date.now()}_1`, action: 'Review and address top scoring gaps', priority: 'high' },
      { id: `action_${Date.now()}_2`, action: 'Complete profile documentation', priority: 'medium' },
    ];
  }

  return {
    message: {
      id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      role: 'assistant',
      content: responseContent,
      timestamp: Date.now(),
      metadata: {
        tokens: 600,
        processingTime: 1500,
        topics,
        actionItems: actionItems.map(a => a.action),
      },
    },
    suggestedQuestions: [
      'How can I improve my lowest scoring dimension?',
      'What questions will investors ask me?',
      'What documents should I have in my data room?',
      'How do I position my weaknesses?',
    ].filter(q => !lowercaseMessage.includes(q.toLowerCase().split(' ')[3] || '')).slice(0, 3),
    actionItems,
    topicsDiscussed: topics,
  };
}

// ============================================================================
// Document Indexing Functions
// ============================================================================

export interface IndexDocumentRequest {
  fileBase64: string;
  fileName: string;
  documentId: string;
  startupId: string;
  mimeType?: string;
  /**
   * Document origin (116.7-03). Set 'data-room' for founder data-room uploads to
   * enable claim extraction; omit / 'pitch-deck' for deck indexing (deck claims
   * are owned by the scoring path). Only 'data-room' triggers extract-document-claims.
   */
  sourceType?: 'data-room' | 'pitch-deck';
}

export interface IndexDocumentStats {
  extractionTimeMs: number;
  chunkingTimeMs: number;
  embeddingTimeMs: number;
  totalTimeMs: number;
  chunkCount: number;
  totalEmbeddingTokens: number;
}

export interface IndexDocumentResponse {
  success: boolean;
  documentId?: string;
  chunkCount?: number;
  processingTimeMs?: number;
  stats?: IndexDocumentStats;
  error?: string;
}

/**
 * Index a document for RAG retrieval
 *
 * Processes the document through extraction → chunking → embedding pipeline
 * and stores chunks in the database for later retrieval during DD Chat.
 *
 * @param request - Document to index
 * @returns Indexing result with chunk count and stats
 */
export async function indexDocument(
  request: IndexDocumentRequest
): Promise<EdgeFunctionResponse<IndexDocumentResponse>> {
  if (mode.isMock) {
    // Simulate processing delay for realistic UX
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }

  return callEdgeFunction<IndexDocumentResponse>('index-document', request, {
    timeout: 60_000,
    mockData: {
      success: true,
      documentId: request.documentId,
      chunkCount: 12,
      processingTimeMs: 2000,
      stats: {
        extractionTimeMs: 500,
        chunkingTimeMs: 200,
        embeddingTimeMs: 1200,
        totalTimeMs: 2000,
        chunkCount: 12,
        totalEmbeddingTokens: 3500,
      },
    },
  });
}

// ============================================================================
// Submission Action Functions
// ============================================================================

export interface RequestMoreInfoRequest {
  submissionId: string;
  message?: string;
}

export interface RequestMoreInfoResponse {
  success: boolean;
}

/**
 * Request more information from a startup that submitted a pitch deck.
 * Sends an email to the contact address with the investor's message.
 */
export async function requestMoreInfo(
  params: RequestMoreInfoRequest
): Promise<EdgeFunctionResponse<RequestMoreInfoResponse>> {
  return callEdgeFunction<RequestMoreInfoResponse>('submission-action', {
    action: 'request-info',
    ...params,
  }, {
    mockData: { success: true },
  });
}

export interface InviteToClaimRequest {
  submissionId: string;
}

export interface InviteToClaimResponse {
  success: boolean;
  claimUrl: string;
}

/**
 * Invite a startup to claim their submission and create an Inverio profile.
 * Generates a unique claim URL and sends it to the contact email.
 */
export async function inviteToClaim(
  params: InviteToClaimRequest
): Promise<EdgeFunctionResponse<InviteToClaimResponse>> {
  return callEdgeFunction<InviteToClaimResponse>('submission-action', {
    action: 'invite-claim',
    ...params,
  }, {
    mockData: {
      success: true,
      claimUrl: 'https://inverio.ai/claim/mock-token',
    },
  });
}

export interface InviteFounderToClaimRequest {
  /** Founder email typed by the investor. */
  email: string;
  companyName: string;
  /** The company_directory / startup_profiles id in scope, if any (best-effort). */
  startupId?: string;
}

export interface InviteFounderToClaimResponse {
  success: boolean;
}

/**
 * Invite the founder of an UNCLAIMED company (found in the directory / dealflow)
 * to claim their company profile. Sends a real invitation email — it does NOT
 * grant the claim (the founder self-verifies ownership from the claim page).
 */
export async function inviteFounderToClaim(
  params: InviteFounderToClaimRequest
): Promise<EdgeFunctionResponse<InviteFounderToClaimResponse>> {
  return callEdgeFunction<InviteFounderToClaimResponse>('directory-invite-claim', params, {
    mockData: { success: true },
  });
}

// ============================================================================
// Claim Submission Functions
// ============================================================================

export interface ValidateClaimRequest {
  action: 'validate';
  token: string;
}

export interface ValidateClaimResponse {
  valid: boolean;
  submission: {
    id: string;
    companyName: string;
    investorName: string;
    scoreSnapshot: {
      overallScore: number;
      confidence: number;
      topRiskFlag: string | null;
    };
    alreadyClaimed: boolean;
    existingAccount: boolean;
    contactEmail: string; // masked
  };
}

export interface ClaimSubmissionRequest {
  action: 'claim';
  token: string;
}

export interface ClaimSubmissionResponse {
  success: boolean;
  alreadyClaimed?: boolean;
  claimedSubmission: {
    id: string;
    submissionId: string;
    companyName: string;
    scoreSnapshot: {
      overallScore: number;
      confidence: number;
      topRiskFlag: string | null;
      breakdown?: Record<string, number>;
      dimensions?: Array<{ name: string; score: number; feedback: string }>;
      recommendations?: string[];
      risks?: Array<{ category: string; description: string }>;
    };
    isVisibleToMarketplace: boolean;
    hasPaidReport: boolean;
    subscriptionTier: string;
  };
}

/**
 * Validate a claim token (no auth required)
 * Returns score snapshot preview for the claim page
 */
export async function validateClaimToken(
  token: string
): Promise<EdgeFunctionResponse<ValidateClaimResponse>> {
  // Special handling: this call does NOT require auth
  // We call the edge function directly without session check
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

  if (mode.isMock) {
    return {
      data: {
        valid: true,
        submission: {
          id: 'mock_submission_id',
          companyName: 'Demo Startup AG',
          investorName: 'Marc',
          scoreSnapshot: {
            overallScore: 72,
            confidence: 68,
            topRiskFlag: 'Team Risk',
          },
          alreadyClaimed: false,
          existingAccount: false,
          contactEmail: 'j***@demo.com',
        },
      },
      error: null,
    };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(`${supabaseUrl}/functions/v1/claim-submission`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
      },
      body: JSON.stringify({ action: 'validate', token }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const data = await response.json();

    if (!response.ok) {
      return { data: null, error: data.error || 'Token validation failed' };
    }

    return { data, error: null };
  } catch (err) {
    reportError(err, { context: 'validateClaimToken', silent: true });
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Claim a submission (requires auth)
 * Links the submission to the authenticated startup account
 */
export async function claimSubmission(
  token: string
): Promise<EdgeFunctionResponse<ClaimSubmissionResponse>> {
  return callEdgeFunction<ClaimSubmissionResponse>('claim-submission', {
    action: 'claim',
    token,
  }, {
    mockData: {
      success: true,
      claimedSubmission: {
        id: 'mock_claim_id',
        submissionId: 'mock_submission_id',
        companyName: 'Demo Startup AG',
        scoreSnapshot: {
          overallScore: 72,
          confidence: 68,
          topRiskFlag: 'Team Risk',
        },
        isVisibleToMarketplace: false,
        hasPaidReport: false,
        subscriptionTier: 'free',
      },
    },
  });
}

/**
 * Claim a submission the founder found through {@link lookupUnclaimedSubmissions},
 * rather than through the emailed link.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE TOKEN
 *   Claim tokens are stored as a SHA-256 hash and the raw value is never
 *   persisted, so a founder who has lost the email cannot be sent it again. The
 *   server accepts a `submissionId` for this case and re-derives the email match
 *   from the submission row itself — it does NOT trust that the lookup filtered
 *   correctly. See _shared/submissions/claim-eligibility.ts.
 */
export async function claimSubmissionById(
  submissionId: string
): Promise<EdgeFunctionResponse<ClaimSubmissionResponse>> {
  return callEdgeFunction<ClaimSubmissionResponse>('claim-submission', {
    action: 'claim',
    submissionId,
  }, {
    mockData: {
      success: true,
      claimedSubmission: {
        id: 'mock_claim_id',
        submissionId: 'mock_submission_id',
        companyName: 'Demo Startup AG',
        scoreSnapshot: { overallScore: 72, confidence: 68, topRiskFlag: 'Team Risk' },
        isVisibleToMarketplace: false,
        hasPaidReport: false,
        subscriptionTier: 'free',
      },
    },
  });
}

/** One unclaimed submission the signed-in founder is eligible to claim. */
export interface UnclaimedSubmissionMatch {
  submissionId: string;
  companyName: string;
  scoredAt: string | null;
  score: number | null;
  confidence: number | null;
  investorName: string;
}

export interface LookupUnclaimedResponse {
  matches: UnclaimedSubmissionMatch[];
}

/**
 * Pitch-deck submissions matching the signed-in founder's own email that nobody
 * has claimed yet.
 *
 * Server-side the match is exact — same address, not same domain — and scoped to
 * `status = 'scored'` and `claimed_by IS NULL`. It is never more permissive than
 * the emailed-link path.
 */
export async function lookupUnclaimedSubmissions(): Promise<
  EdgeFunctionResponse<LookupUnclaimedResponse>
> {
  return callEdgeFunction<LookupUnclaimedResponse>('lookup-unclaimed-submission', {}, {
    mockData: {
      matches: [
        {
          submissionId: 'mock_submission_id',
          companyName: 'Demo Startup AG',
          scoredAt: new Date().toISOString(),
          score: 72,
          confidence: 68,
          investorName: 'Sarah Chen',
        },
      ],
    },
  });
}

// ============================================================================
// Rescore Submission Functions
// ============================================================================

export interface RescoreSubmissionRequest {
  claimedSubmissionId: string;
}

export interface RescoreSubmissionResponse {
  success: boolean;
  /**
   * True when the scoring no-op gate reused the prior score — the input (profile
   * + data-room evidence + methodology) was unchanged, so no fresh LLM run
   * happened. The UI shows an "already up to date" message instead of a delta.
   * A new/updated document or a newly-verified claim would have produced a real
   * re-score (cached: false).
   */
  cached?: boolean;
  /** Human-facing explanation accompanying a `cached` no-op. */
  message?: string;
  score: {
    overallScore: number;
    confidence?: number;
    topRiskFlag?: string | null;
    scoreDelta?: number;
    previousScore?: number;
    dimensions?: Array<{ name: string; score: number; feedback: string }>;
    recommendations?: string[];
    risks?: Array<{ category: string; description: string }>;
    rescoredAt?: string;
    rescoreCount?: number;
  };
}

/**
 * Rescore a claimed submission (requires starter+ subscription)
 */
export async function rescoreSubmission(
  params: RescoreSubmissionRequest
): Promise<EdgeFunctionResponse<RescoreSubmissionResponse>> {
  return callEdgeFunction<RescoreSubmissionResponse>('rescore-submission', params, {
    timeout: 90_000,
    mockData: {
      success: true,
      score: {
        overallScore: 76,
        confidence: 72,
        topRiskFlag: 'Team Risk',
        scoreDelta: 4,
        previousScore: 72,
        dimensions: [
          { name: 'Team', score: 70, feedback: 'Experienced team with relevant domain expertise' },
          { name: 'Market', score: 75, feedback: 'Large addressable market with clear demand' },
          { name: 'Product', score: 80, feedback: 'Strong product-market fit signals' },
          { name: 'Traction', score: 65, feedback: 'Early traction, growing user base' },
          { name: 'Financials', score: 68, feedback: 'Unit economics improving' },
        ],
        recommendations: [
          'Strengthen go-to-market strategy documentation',
          'Add customer testimonials to build evidence',
          'Document competitive moat more clearly',
        ],
        risks: [
          { category: 'Team Risk', description: 'No dedicated sales leadership yet' },
          { category: 'Market Risk', description: 'Competitive landscape intensifying' },
        ],
        rescoredAt: new Date().toISOString(),
        rescoreCount: 1,
      },
    },
  });
}

// ============================================================================
// Visibility Toggle Functions
// ============================================================================

export interface ToggleVisibilityRequest {
  claimedSubmissionId: string;
  isVisible: boolean;
}

export interface ToggleVisibilityResponse {
  success: boolean;
  isVisible: boolean;
  message: string;
}

/**
 * Toggle marketplace visibility for a claimed submission (requires standard+)
 */
export async function toggleSubmissionVisibility(
  params: ToggleVisibilityRequest
): Promise<EdgeFunctionResponse<ToggleVisibilityResponse>> {
  return callEdgeFunction<ToggleVisibilityResponse>('toggle-visibility', params, {
    mockData: {
      success: true,
      isVisible: params.isVisible,
      message: params.isVisible
        ? 'Your score is now visible in the marketplace'
        : 'Your score is now private',
    },
  });
}
